import React, { useMemo, useCallback } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import styles from '../../styles/automation/results/resultsPanel.module.scss';

const EXECUTION_STATUS = {
    IN_PROGRESS: 'In Progress',
    FAILED: 'Failed',
    PASSED: 'Passed',
};

const SummaryTile = React.memo(({ label, value, statusClass }) => (
    <div className={`${styles.tileSummaryItem} ${statusClass || ''}`}>
        <div className={styles.tileSummaryLabel}>{label}</div>
        <div className={styles.tileSummaryValue}>{value}</div>
    </div>
));

const CompactSummaryItem = React.memo(({ label, value, statusClass }) => (
    <div className={styles.compactSummaryItem}>
        <span className={styles.compactSummaryLabel}>{label}</span>
        <span className={`${styles.compactSummaryValue} ${statusClass || ''}`}>
            {value}
        </span>
    </div>
));

const EditButton = React.memo(({ onClick }) => (
    <button 
        className={styles.editButton}
        onClick={onClick}
        aria-label="Edit in Automation Studio"
        title="Opens Automation Studio "
    >
        <span className={styles.editButtonText}>Edit in Automation Studio</span>
        <ArrowTopRightOnSquareIcon className="icon" style={{ width: '14px', height: '14px' }} />
    </button>
));

const ExecutionSummaryHeader = ({ 
    testSummary, 
    variant = 'card', 
    isReplayPage = false, 
    onEdit, 
    showEdit = false, 
    isRunning = false,
    isAiDynamicRun = false,
}) => {
    if (!testSummary) return null;

    const safeTestSummary = useMemo(() => ({
        totalSteps: testSummary.totalSteps ?? 0,
        passed: testSummary.passed ?? 0,
        failed: testSummary.failed ?? 0,
    }), [testSummary.totalSteps, testSummary.passed, testSummary.failed]);

    const overallStatus = useMemo(() => {
        if (isRunning) {
            return EXECUTION_STATUS.IN_PROGRESS;
        }
        if (safeTestSummary.failed > 0) {
            return EXECUTION_STATUS.FAILED;
        }
        return EXECUTION_STATUS.PASSED;
    }, [safeTestSummary.failed, isRunning]);

    const statusStyleMap = useMemo(() => ({
        [EXECUTION_STATUS.IN_PROGRESS]: styles.tileRunning,
        [EXECUTION_STATUS.FAILED]: styles.tileFailed,
        [EXECUTION_STATUS.PASSED]: styles.tilePassed,
    }), []);

    const handleEdit = useCallback((event) => {
        if (onEdit) onEdit(event);
    }, [onEdit]);

    if (variant === 'tiles') {
        return (
            <>
                {isAiDynamicRun && (
                    <div className={styles.aiExecutionModeNote} role="status">
                        This run used <strong>AI mode</strong> (dynamic instructions from your automation text — not fixed script replay).
                    </div>
                )}
                <div className={`${styles.tileSummaryWrapper} ${isReplayPage ? styles.replay_page : ''}`}>
                <div className={styles.tileSummaryContainer}>
                    <SummaryTile
                        label="Overall Status:"
                        value={overallStatus}
                        statusClass={statusStyleMap[overallStatus]}
                    />
                    <SummaryTile
                        label="Total"
                        value={safeTestSummary.totalSteps}
                    />
                    <SummaryTile
                        label="Passed"
                        value={safeTestSummary.passed}
                        statusClass={styles.tilePassed}
                    />
                    <SummaryTile
                        label="Failed"
                        value={safeTestSummary.failed}
                        statusClass={styles.tileFailed}
                    />
                </div>
                {showEdit && onEdit && <EditButton onClick={handleEdit} />}
            </div>
            </>
        );
    }

    return (
        <div className={variant === 'card' ? styles.compactSummaryHeader : styles.compactSummaryHeaderNoCard}>
            <div className={styles.compactSummaryContent}>
                <CompactSummaryItem
                    label="Total Steps"
                    value={safeTestSummary.totalSteps}
                />
                <CompactSummaryItem
                    label="Steps Passed"
                    value={safeTestSummary.passed}
                    statusClass={styles.passed}
                />
                <CompactSummaryItem
                    label="Steps Failed"
                    value={safeTestSummary.failed}
                    statusClass={styles.failed}
                />
            </div>
        </div>
    );
};

export default ExecutionSummaryHeader;
